import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { connectMessaging, onMessagingEvent, sendTyping } from '@/lib/messagingSocket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Send, Search, Users, Pin, MessageSquare, Circle, ExternalLink, MessagesSquare, Plus } from 'lucide-react';
import UserAvatar from '@/components/UserAvatar';
import { DiscussionThreadDialog, StartDiscussionDialog } from '@/components/DiscussionDialog';

const STATUS_COLOR = { active: 'text-emerald-400', idle: 'text-amber-400', away: 'text-rose-400', offline: 'text-muted-foreground/70' };
const QUICK = ['👍', '❤️', '🎉', '👀', '✅'];

function initials(name) { return (name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const me = user?.user_id;
  const [params] = useSearchParams();
  const [presence, setPresence] = useState({});
  const [people, setPeople] = useState([]);
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [typingUsers, setTypingUsers] = useState({});
  const [search, setSearch] = useState('');
  const [discussions, setDiscussions] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const scrollRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const loadConvs = async () => { const r = await api.getConversations(); setConvs(r.conversations || []); return r.conversations || []; };
  const loadPeople = async () => { const r = await api.getPresence(); setPeople(r.users || []); };
  const loadDiscussions = async () => {
    try { const r = await api.getMyDiscussions(); setDiscussions(Array.isArray(r) ? r : (r?.discussions || [])); }
    catch { /* non-fatal */ }
  };
  const openThread = (id) => { setThreadId(id); setActiveId(null); };
  // Where a discussion originated, shown as a small badge in the list.
  const discSource = (d) => {
    const src = d.report_state?.source || d.source;
    if (src === 'ai_explore' || src === 'ai-explore') return 'Explore';
    if (src === 'investigation') return 'Investigation';
    if (src === 'kpi' || src === 'kpi_monitor') return 'KPI';
    if (d.report_name) return d.report_name;
    if (d.report_id) return 'Dashboard';
    return 'Discussion';
  };

  const openConversation = async (id) => {
    setThreadId(null);
    setActiveId(id);
    const r = await api.getMessages(id);
    setMessages(r.messages || []);
    await api.markConversationRead(id);
    setConvs(cs => cs.map(c => c.conversation_id === id ? { ...c, unread: 0 } : c));
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  useEffect(() => {
    connectMessaging();
    loadPeople(); loadConvs(); loadDiscussions();
    const off = onMessagingEvent((evt) => {
      if (evt.type === 'presence') setPresence(evt.presence || {});
      else if (evt.type === 'message') {
        if (evt.conversation_id === activeIdRef.current) {
          setMessages(m => [...m, evt.message]);
          api.markConversationRead(evt.conversation_id);
          setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
        }
        loadConvs();
      } else if (evt.type === 'typing' && evt.conversation_id === activeIdRef.current) {
        setTypingUsers(t => ({ ...t, [evt.user_id]: evt.typing ? evt.name : undefined }));
      } else if (evt.type === 'reaction') {
        setMessages(m => m.map(x => x.message_id === evt.message_id ? { ...x, reactions: evt.reactions } : x));
      } else if (evt.type === 'pin') {
        setMessages(m => m.map(x => x.message_id === evt.message_id ? { ...x, pinned: evt.pinned } : x));
      }
    });
    return () => off();
     
  }, []);

  // deep-link: ?dm=<user_id> or ?conversation=<id>
  useEffect(() => {
    const dm = params.get('dm'); const cv = params.get('conversation');
    (async () => {
      if (cv) openConversation(cv);
      else if (dm) { const c = await api.createConversation({ type: 'dm', participant_ids: [dm] }); await loadConvs(); openConversation(c.conversation_id); }
    })();
     
  }, [params]);

  // deep-link: ?id=<discussion_id> opens a discussion thread (e.g. started from
  // AI Explore / a dashboard / an investigation).
  useEffect(() => {
    const id = params.get('id');
    if (id) openThread(id);
  }, [params]);

  const startDm = async (uid) => {
    const c = await api.createConversation({ type: 'dm', participant_ids: [uid] });
    await loadConvs(); openConversation(c.conversation_id);
  };

  const send = async () => {
    const t = text.trim(); if (!t || !activeId) return;
    setText('');
    const mentions = people.filter(p => t.includes(`@${(p.name || '').split(' ')[0]}`)).map(p => p.user_id);
    try { await api.sendMessage(activeId, { text: t, mentions }); } catch (e) { toast.error(e.message || 'Failed to send'); }
  };

  const react = async (mid, emoji) => { try { await api.toggleMessageReaction(mid, emoji); } catch { /* */ } };
  const pin = async (mid) => { try { await api.toggleMessagePin(mid); } catch { /* */ } };

  const activeConv = convs.find(c => c.conversation_id === activeId);
  const pinned = useMemo(() => messages.filter(m => m.pinned), [messages]);
  const statusOf = (uid, fallback) => presence[uid]?.status || fallback || 'offline';
  const typingList = Object.values(typingUsers).filter(Boolean);

  const searchResults = search.trim()
    ? convs.filter(c => (c.title || '').toLowerCase().includes(search.toLowerCase()))
    : convs;

  return (
    <div className="h-[calc(100vh-64px)] flex gap-3 p-4" data-testid="messages-page">
      {/* Left: people + conversations */}
      <div className="w-72 shrink-0 flex flex-col gap-3 min-h-0">
        <div className="glass-card rounded-xl p-3 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2 text-sm font-heading font-semibold"><Users className="w-4 h-4 text-indigo-400" /> Workspace</div>
          <div className="overflow-y-auto space-y-1 flex-1">
            {people.map(p => (
              <button key={p.user_id} onClick={() => p.user_id !== me && startDm(p.user_id)} data-testid={`presence-${p.user_id}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/40 text-left">
                <div className="relative">
                  <UserAvatar name={p.name} src={p.avatarUrl} size={28} />
                  <Circle className={`w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 fill-current ${STATUS_COLOR[statusOf(p.user_id, p.status)]}`} />
                </div>
                <div className="min-w-0"><p className="text-xs text-foreground truncate">{p.name}{p.user_id === me ? ' (you)' : ''}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{statusOf(p.user_id, p.status)}</p></div>
              </button>
            ))}
          </div>
        </div>
        <div className="glass-card rounded-xl p-3 flex-1 min-h-0 flex flex-col">
          <div className="relative mb-2"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search chats" className="h-8 pl-7 text-xs" data-testid="messages-search" /></div>
          <div className="overflow-y-auto space-y-1 flex-1">
            {searchResults.map(c => (
              <button key={c.conversation_id} onClick={() => openConversation(c.conversation_id)} data-testid={`conv-${c.conversation_id}`}
                className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-left ${activeId === c.conversation_id ? 'bg-muted/50' : 'hover:bg-muted/30'}`}>
                <div className="min-w-0 flex items-center gap-2">
                  {c.type === 'object' ? <MessageSquare className="w-3.5 h-3.5 text-cyan-400 shrink-0" /> : <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />}
                  <div className="min-w-0"><p className="text-xs text-foreground truncate">{c.title}</p>
                    {c.last_text && <p className="text-[10px] text-muted-foreground truncate">{c.last_text}</p>}</div>
                </div>
                {c.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>}
              </button>
            ))}
            {searchResults.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No conversations yet — click a teammate to start.</p>}
          </div>
        </div>
        {/* Discussions — group threads started here or from dashboards, reports, Explore, investigations */}
        <div className="glass-card rounded-xl p-3 flex-1 min-h-0 flex flex-col" data-testid="messages-discussions">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-heading font-semibold"><MessagesSquare className="w-4 h-4 text-violet-400" /> Discussions</div>
            <button onClick={() => setNewOpen(true)} className="flex items-center gap-1 text-[10px] px-1.5 py-1 rounded-md bg-primary/15 text-primary hover:bg-primary/25" data-testid="new-discussion-btn"><Plus className="w-3 h-3" /> New</button>
          </div>
          <div className="overflow-y-auto space-y-1 flex-1">
            {(search.trim() ? discussions.filter(d => ((d.title || '') + ' ' + (d.question || '')).toLowerCase().includes(search.toLowerCase())) : discussions).map(d => (
              <button key={d.discussion_id} onClick={() => openThread(d.discussion_id)} data-testid={`discussion-${d.discussion_id}`}
                className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-left ${threadId === d.discussion_id ? 'bg-muted/50' : 'hover:bg-muted/30'}`}>
                <div className="min-w-0 flex items-center gap-2">
                  <MessagesSquare className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-foreground truncate">{d.title || d.question}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{discSource(d)}{(d.assigned_to?.length || 0) > 0 ? ` · ${d.assigned_to.length} member${d.assigned_to.length > 1 ? 's' : ''}` : ''}</p>
                  </div>
                </div>
                {(d.unread_replies || 0) > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">{d.unread_replies}</span>}
              </button>
            ))}
            {discussions.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No discussions yet. Start one here, or from a dashboard, report or investigation.</p>}
          </div>
        </div>
      </div>

      {/* Right: thread (chat conversation OR a selected discussion, inline) */}
      <div className="flex-1 glass-card rounded-xl flex flex-col min-h-0" data-testid="messages-thread">
        {threadId ? (
          <DiscussionThreadDialog
            inline
            open={!!threadId}
            discussionId={threadId}
            onOpenChange={(o) => { if (!o) { setThreadId(null); loadDiscussions(); } }}
          />
        ) : !activeConv ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a conversation or teammate to start messaging.</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border/40">
              <p className="font-heading font-semibold text-foreground">{activeConv.title}</p>
              {activeConv.type === 'object' && <p className="text-[10px] text-cyan-300">Attached to {activeConv.object_type}</p>}
            </div>
            {pinned.length > 0 && (
              <div className="px-4 py-2 border-b border-border/30 bg-amber-500/5">
                {pinned.map(p => <p key={p.message_id} className="text-[11px] text-amber-200/90 flex items-center gap-1"><Pin className="w-3 h-3" />{p.text}</p>)}
              </div>
            )}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(m => {
                const mine = m.sender_id === me;
                return (
                  <div key={m.message_id} className={`group flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.message_id}`}>
                    <div className={`max-w-[70%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                      {!mine && <span className="text-[10px] text-muted-foreground mb-0.5 flex items-center gap-1"><UserAvatar name={m.sender_name || m.sender?.name} src={m.sender?.avatarUrl} size={16} />{m.sender_name || m.sender?.name}</span>}
                      <div className="relative pb-2.5">
                        <div className={`rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-indigo-500/25 text-foreground' : 'bg-muted/50 text-foreground'}`}>{m.text}</div>
                        {m.reactions && Object.keys(m.reactions).length > 0 && (
                          <div className={`absolute -bottom-2.5 ${mine ? 'right-2' : 'left-2'} flex gap-1 z-10`} data-testid={`reactions-${m.message_id}`}>
                            {Object.entries(m.reactions).map(([e, us]) => (
                              <button key={e} onClick={() => react(m.message_id, e)}
                                className="flex items-center gap-0.5 text-[10px] leading-none bg-card border border-border/60 shadow-sm rounded-full px-1.5 py-0.5 hover:bg-muted/60 transition-colors">
                                <span>{e}</span><span className="text-muted-foreground">{us.length}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {m.attachment?.type === 'shared_view' && (
                        <Button size="sm" variant="outline" className="mt-1 h-7 text-[11px] gap-1 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                          onClick={() => navigate(m.attachment.route)} data-testid={`open-shared-view-${m.message_id}`}>
                          <ExternalLink className="w-3 h-3" /> Open this view{m.attachment.label ? `: ${m.attachment.label}` : ''}
                        </Button>
                      )}
                      <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {QUICK.map(e => <button key={e} onClick={() => react(m.message_id, e)} className="text-xs hover:scale-125 transition-transform">{e}</button>)}
                        <button onClick={() => pin(m.message_id)} className="text-muted-foreground hover:text-amber-300" title="Pin"><Pin className="w-3 h-3" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No messages yet. Say hello 👋</p>}
            </div>
            {typingList.length > 0 && <p className="px-4 py-1 text-[10px] text-muted-foreground italic">{typingList.join(', ')} typing…</p>}
            <div className="p-3 border-t border-border/40 flex items-center gap-2">
              <Input value={text} onChange={e => { setText(e.target.value); sendTyping(activeId, !!e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Message… use @ to mention" className="flex-1" data-testid="message-input" />
              <Button onClick={send} disabled={!text.trim()} data-testid="message-send"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </div>

      <StartDiscussionDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(disc) => { loadDiscussions(); if (disc?.discussion_id) openThread(disc.discussion_id); }}
      />
    </div>
  );
}
